import { CATEGORIES, type Category } from "./categories";
import { RATE_CODE, MAX_SETTING_VALUE } from "./cogmRates";

/**
 * rmcConsumption — how many YARDS of fabric a set consumes, by product line.
 *
 * WHY THIS HAD TO BE TYPED IN. There is no bill of materials anywhere in this
 * system. `Main_MasterFile` records the yardage of a roll as it was received
 * and `Inventory` keeps a running yardage on hand, but nothing states what a
 * set of SS028 costs in cloth — the RMC module already admits as much, which
 * is why `AnnualDemandOverride` exists (a hand-typed stand-in "via a BOM usage
 * rate" the system does not have). Without a number here, the question "are
 * the raw materials enough for these orders" has no arithmetic behind it.
 *
 * WHAT THE FLOOR ANSWERED (Mikko, 2026-09-10). ELITE PRO takes 3 yards a set
 * and is cut from 4 Way Stretch; CLASSIC, ELITE, ALTA and PRIMO take 4.5 and
 * are cut from Lacoste, Coloured or White. NURSE UNIFORM and DOCTORS COAT
 * were explicitly left out — nobody has measured them yet — so they ship
 * UNRATED and their sets are counted apart rather than assumed to be 4.5 off
 * Lacoste like their neighbours. Those are the shipped figures below. They are
 * still EDITABLE — a rate card that needs a deploy to change is a card that
 * goes stale in a spreadsheet nobody else can see — and a line can be blanked
 * back to UNRATED, whose sets are then counted apart rather than costed at a
 * guess, the way costing.ts counts unpriced sets.
 *
 * TWO FABRICS, NOT ONE POOL. Yardage cannot be netted across the two: a
 * warehouse holding 25,000 yards of 4 Way Stretch and none of Lacoste has
 * plenty of cloth and can cut nothing but ELITE PRO. So a line also carries
 * the FAMILY it is cut from, requirement is totalled per family, and each is
 * matched only against its own stock.
 *
 * PER LINE, NOT PER DESIGN. Seven numbers somebody will actually maintain beat
 * ninety-two nobody will. A line is a coherent cut — an ALTA set is an ALTA
 * set — and where a design is a genuine outlier the answer is a rate card with
 * more lines on it, not a blank screen.
 *
 * FABRIC IS SPENT AT CUTTING, which is what makes the figure usable: only sets
 * that have NOT been cut still need cloth. A set already through BQC2 has had
 * its yardage consumed whatever happens to it downstream.
 *
 * Stored in mfg settings under one key, shared by everyone, alongside the COGM
 * rate card and serialised the same way (see cogmRates.ts) — including the
 * two-letter line codes, so the two cards cannot drift apart on what "EP"
 * means.
 */

/** Yards per set for the lines that have one. A line absent from the map is
 * UNRATED — distinct from a line rated 0, which would claim a set of it uses
 * no cloth at all. */
export type YardsPerSet = Partial<Record<Category, number>>;

export interface IRmcConsumptionCard {
  yardsPerSet: YardsPerSet;
  /** `YYYY-MM-DD` the card took effect. */
  effective: string;
  /** Who saved it. Empty on the shipped card — nobody saved it. */
  setBy: string;
  /** False until somebody has filled one in. Screens say which. */
  stored: boolean;
}

/**
 * The fabric each product line is cut from.
 *
 * `OTHER` is not a family anybody cuts to — it is the label for fabric on the
 * RMC shelf that neither family claims (Superfine, Armani), kept visible so a
 * roll is never quietly counted as supply for a line that cannot use it.
 */
export const FABRIC_FAMILIES = ["4WAY", "LACOSTE"] as const;
export type FabricFamily = (typeof FABRIC_FAMILIES)[number];

export const FAMILY_LABEL: Record<FabricFamily, string> = {
  "4WAY": "4 Way Stretch",
  LACOSTE: "Lacoste (Coloured + White)",
};

/**
 * Which RMC catalog items count as each family's stock.
 *
 * Matched against the item's NAME and its catalog category, because the two
 * carry the same fact in different words — "4 Way Stretch Fabric" is filed
 * under STRETCHFINE, "Lacoste Colored" and "Lacoste White" under LACOSTE — and
 * a rename of either must not silently zero a family's supply.
 */
export const FAMILY_MATCH: Record<FabricFamily, RegExp> = {
  "4WAY": /4\s*-?\s*way|stretchfine/i,
  LACOSTE: /lacoste/i,
};

/**
 * Which family a product line is cut from.
 *
 * NURSE UNIFORM and DOCTORS COAT are filed under Lacoste because that is what
 * the floor cuts them from; the family and the yardage are separate facts, and
 * an unrated line still belongs to a bolt. It contributes nothing to that
 * bolt's requirement until somebody types a yardage.
 */
export const FAMILY_OF: Record<Category, FabricFamily> = {
  CLASSIC: "LACOSTE",
  ELITE: "LACOSTE",
  ALTA: "LACOSTE",
  PRIMO: "LACOSTE",
  "ELITE PRO": "4WAY",
  "NURSE UNIFORM": "LACOSTE",
  "DOCTORS COAT": "LACOSTE",
};

/**
 * The shipped card — the floor's own figures, 2026-09-10.
 *
 * ELITE PRO is the odd one out on both counts: a lighter 3 yards, off a
 * different bolt. Everything else is 4.5 off Lacoste.
 */
export const DEFAULT_YARDS_PER_SET: YardsPerSet = {
  CLASSIC: 4.5,
  ELITE: 4.5,
  ALTA: 4.5,
  PRIMO: 4.5,
  "ELITE PRO": 3,
  // Deliberately absent, not zero — see the header. Fill them in on screen and
  // they join the requirement; until then their sets show as UNRATED.
};

export const DEFAULT_CONSUMPTION_EFFECTIVE = "2026-09-10";

export const SHIPPED_CONSUMPTION_CARD: IRmcConsumptionCard = {
  yardsPerSet: DEFAULT_YARDS_PER_SET,
  effective: DEFAULT_CONSUMPTION_EFFECTIVE,
  setBy: "",
  stored: false,
};

export const YARDS_MIN = 0;
/** A ceiling, not a limit anyone will meet. A set is a garment, not a bolt;
 * this exists so a mistyped 250 is refused at the form rather than reported as
 * a fabric shortage nobody can find. */
export const YARDS_MAX = 100;

export type YardsProblem = "not-a-number" | "not-positive" | "too-large" | "too-precise";

/** Whether one typed yardage can be stored. */
export function validateYards(v: number): YardsProblem | null {
  if (!Number.isFinite(v)) return "not-a-number";
  // Zero is refused rather than stored: "this line uses no cloth" is never
  // what anybody means, and it would silently drop the line out of every
  // requirement total while looking like a filled-in answer.
  if (v <= YARDS_MIN) return "not-positive";
  if (v > YARDS_MAX) return "too-large";
  // Three decimals — a thousandth of a yard is already finer than anything a
  // cutting table measures, and more would print rounded and total unrounded.
  if (Math.abs(v * 1000 - Math.round(v * 1000)) > 1e-9) return "too-precise";
  return null;
}

export const YARDS_MESSAGE: Record<YardsProblem, string> = {
  "not-a-number": "is not a number.",
  "not-positive": "must be more than zero — leave it blank to mark the line unrated.",
  "too-large": `is over ${YARDS_MAX} yards a set — check for a stray digit.`,
  "too-precise": "has more than three decimal places.",
};

/**
 * `v1|YYYY-MM-DD|EL:2.1|AL:2.35|…`
 *
 * Unrated lines are OMITTED rather than written as 0 — the format has to be
 * able to say "nobody has answered for this line", because that is the state
 * the card ships in and the state a screen has to report. Throws past the
 * 255-character cap for the same reason the COGM card does: a truncated card
 * parses as a valid card with lines missing, and a missing line here reads as
 * "no fabric needed".
 */
export function serialiseConsumption(
  card: { yardsPerSet: YardsPerSet; effective: string },
): string {
  const parts = CATEGORIES
    .filter((c) => typeof card.yardsPerSet[c] === "number")
    .map((c) => `${RATE_CODE[c]}:${trimNum(card.yardsPerSet[c] as number)}`);
  const out = `v1|${card.effective}|${parts.join("|")}`;
  if (out.length > MAX_SETTING_VALUE) {
    throw new Error(
      `Consumption card is ${out.length} characters and the settings row holds ${MAX_SETTING_VALUE}. ` +
      `Nothing was saved — shorten the figures rather than losing a product line.`,
    );
  }
  return out;
}

function trimNum(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

const BY_CODE = new Map<string, Category>(
  (Object.entries(RATE_CODE) as [Category, string][]).map(([cat, code]) => [code, cat]),
);

/**
 * Reads a stored card back.
 *
 * A line the string does not mention stays UNRATED rather than inheriting the
 * shipped figure. That is the opposite of what parseCogmRates does, and
 * deliberately so: blanking a line is the only way to SAY a line is unrated,
 * and a format that healed the blank back to a default would make the state
 * unreachable — somebody who deletes a figure because it is wrong would find
 * the old one still in force. Returns null only when the string is not a
 * consumption card at all, in which case the caller falls back to the shipped
 * card whole.
 */
export function parseConsumption(raw: string | null | undefined): IRmcConsumptionCard | null {
  if (!raw) return null;
  const parts = raw.split("|");
  if (parts[0] !== "v1" || parts.length < 2) return null;
  const effective = /^\d{4}-\d{2}-\d{2}$/.test(parts[1]) ? parts[1] : "";
  const yardsPerSet: YardsPerSet = {};
  let any = false;
  for (const p of parts.slice(2)) {
    const [code, v] = p.split(":");
    const cat = BY_CODE.get(code);
    if (!cat) continue;
    const n = Number(v);
    if (validateYards(n) !== null) continue;
    yardsPerSet[cat] = n;
    any = true;
  }
  if (!any) return null;
  return { yardsPerSet, effective, setBy: "", stored: true };
}

/** Lines still waiting for a figure — what a screen names so "unrated" is a
 * list of things to do rather than a count. */
export function unratedLines(card: IRmcConsumptionCard): Category[] {
  return CATEGORIES.filter((c) => typeof card.yardsPerSet[c] !== "number");
}
